import { useSuspenseQuery } from '@apollo/client';
import React, { Suspense } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { CopyTextButton } from '@eluve/blocks';
import {
  HStack,
  LinkButton,
  LoadingIndicator,
  PageTitle,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import {
  OpenInAppButton,
  OpenInReviewButton,
  TabNav,
} from '@eluve/smart-blocks';

const appointmentNameQuery = graphql(`
  query appointmentName($tenantId: uuid!, $appointmentId: uuid!) {
    appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
      __typename
      id
      name
      createdAt
    }
  }
`);

export const AdminAppointmentOutlet: React.FC = () => {
  const { tenantId, appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };

  const {
    data: { appointmentsByPk: appointment },
  } = useSuspenseQuery(appointmentNameQuery, {
    variables: { tenantId, appointmentId },
  });

  const datadogBaseUrl = 'https://us5.datadoghq.com/logs';
  const url = new URL(datadogBaseUrl);
  url.searchParams.set('query', `@appointmentId:${appointmentId}`);

  // Set default time range to the appointment's creation time to now
  if (appointment) {
    const datadogRangeStart = new Date(appointment.createdAt).getTime();
    const datadogRangeEnd = Date.now();
    url.searchParams.set('from_ts', datadogRangeStart.toString());
    url.searchParams.set('to_ts', datadogRangeEnd.toString());
    url.searchParams.set('live', 'false'); // Disable live tailing
  }

  const datadogUrl = url.toString();

  return (
    <div>
      <HStack align="center" justify="between">
        <HStack justify="end" wFull={false}>
          <PageTitle>{appointment?.name}</PageTitle>
          <CopyTextButton copyText={appointmentId} />
        </HStack>
        <HStack justify="end" wrap wFull={false}>
          <OpenInAppButton appointmentId={appointmentId} tenantId={tenantId} />
          <OpenInReviewButton
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
          <LinkButton
            href={datadogUrl}
            icon={{ name: 'Bone' }}
            text="View Datadog Logs"
            type="outline"
          />
        </HStack>
      </HStack>
      <TabNav
        tabs={[
          {
            name: 'Details',
            link: 'details',
          },
          {
            name: 'Summary and Transcript',
            link: 'artifacts',
          },
          {
            name: 'Files',
            link: 'files',
          },
          {
            name: 'Live vs Batch Transcript Comparison',
            link: 'transcript-comparison',
          },
          {
            name: 'Source Artifacts',
            link: 'source-artifacts',
          },
        ]}
      />
      <div className="mt-4">
        <Suspense fallback={<LoadingIndicator />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
};
